import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { parseMoneyInput } from '../../lib/formatters.ts'
import MoneyInput from '../../components/MoneyInput'
import { dollarsToWords } from '../../lib/dollarsToWords.ts'
import { localDateKey } from '../../lib/localDate.ts'
import { bankConfirmMode, bankSaveEnabled, bankAsOfDefault, bankSaveLabel } from './cashOnHandConfirm.ts'

type Props = {
  open: boolean
  fromVoice?: boolean
  defaultCents?: number | null
  saveError?: boolean
  onClose: () => void
  onSave: (input: {
    kind: 'cash_on_hand'
    amount_cents: number
    effective_date: string
    project_id: null
  }) => Promise<void>
}

export default function CashOnHandCard({
  open, fromVoice = false, defaultCents, saveError = false, onClose, onSave,
}: Props) {
  const [amount, setAmount] = useState(
    defaultCents != null ? (Number(defaultCents) / 100).toFixed(2) : '',
  )
  const [asOf, setAsOf] = useState(bankAsOfDefault())
  const [voiceOrigin, setVoiceOrigin] = useState(fromVoice)
  const [wordsOk, setWordsOk] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!open) return
    setAmount(defaultCents != null ? (Number(defaultCents) / 100).toFixed(2) : '')
    setAsOf(bankAsOfDefault())
    setVoiceOrigin(fromVoice)
    setWordsOk(false)
    setError(false)
  }, [open, defaultCents, fromVoice])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const dollars = parseMoneyInput(amount)
  const mode = bankConfirmMode(voiceOrigin, dollars)
  const canSave = bankSaveEnabled(amount, asOf)

  async function submit() {
    if (!canSave) return
    setSaving(true)
    setError(false)
    try {
      await onSave({
        kind: 'cash_on_hand',
        amount_cents: Math.round(dollars * 100),
        effective_date: asOf || localDateKey(),
        project_id: null,
      })
      onClose()
    } catch {
      setError(true)
    } finally {
      setSaving(false)
    }
  }

  return createPortal(
    <div className="cr-scrim cr-bank-scrim" data-testid="cr-cash-on-hand-card">
      <form
        className="cr-bank-card"
        role="dialog"
        aria-labelledby="cr-bank-heading"
        onSubmit={(e) => { e.preventDefault(); void submit() }}
      >
        <div className="cr-bank-head">
          <h2 id="cr-bank-heading">{"What's in the bank today?"}</h2>
          <button type="button" className="cr-bank-x" aria-label="Close" onClick={onClose} data-testid="cr-bank-close">
            ×
          </button>
        </div>
        <label className="cr-bank-field">
          <span>Amount</span>
          <span className="cr-currency-input">
            <span aria-hidden>$</span>
            <MoneyInput
              aria-label="Cash on hand"
              value={amount}
              onChange={(v) => { setAmount(v); setVoiceOrigin(false); setWordsOk(false) }}
              data-testid="cr-bank-amount"
            />
          </span>
          {mode !== 'none' ? (
            <small data-testid="cr-words-readback">{dollarsToWords(dollars)}</small>
          ) : null}
        </label>
        <label className="cr-bank-field">
          <span>As of</span>
          <input
            aria-label="As of"
            type="date"
            value={asOf}
            max={bankAsOfDefault()}
            onChange={(e) => setAsOf(e.target.value)}
            data-testid="cr-bank-as-of"
          />
        </label>
        {mode === 'voice-check' ? (
          <label className="cr-words">
            <input
              type="checkbox"
              checked={wordsOk}
              onChange={(e) => setWordsOk(e.target.checked)}
              data-testid="cr-words-check"
            />
            I heard that correctly
          </label>
        ) : null}
        {error || saveError ? <p className="cr-neg" role="alert">Save failed</p> : null}
        <div className="cr-bank-actions">
          <button type="submit" className="cr-btn dark" disabled={!canSave || saving} data-testid="cr-bank-save">
            {saving ? 'Saving…' : bankSaveLabel(asOf)}
          </button>
          <button type="button" className="cr-btn" onClick={onClose} data-testid="cr-bank-cancel">Cancel</button>
        </div>
      </form>
    </div>,
    document.body,
  )
}
